








































import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import VuePapaParse from 'vue-papa-parse';
import API, { Types } from '@/modules/API';
import Utils from '@/modules/Utils';
import PropUtils from '@/modules/PropUtils';
import cloneDeep from 'lodash/cloneDeep';
import { Global } from '@/store';

Vue.use(VuePapaParse);
@Component({})
class ExportData extends Vue {
  @Global.State('timezone') timezone;
  @Global.State('lang') lang;

  @Prop({ default: () => [] }) data;
  @Prop() color;
  @Prop() text;
  @Prop() sourceType: string;
  @Prop() sourceTitle?: string;
  @Prop() isGroups?: boolean;
  @Prop() isDisabled: boolean;
  @Prop() noParsing: boolean;
  @Prop() removeDate;

  formats = ['csv', 'xlsx', 'json'];
  exportedData = [];
  loading = false;
  RETROFIT_MASKED_FIELDS = ['sys___active', 'sys___connected', 'sys___online'];
  MIN_COLUMN_WIDTH = 12;
  MAX_COLUMN_WIDTH = 50;
  WIDTH_SAMPLE_ROWS = 500;
  headersMap = {
    'id': 'Cloud ID',
    'rssi': 'RSSI [dBm]',
    'power': 'Dali power',
    'name': 'Name',
    'class_name': 'Device class',
    'meta.category.category': 'Category',
    // 'Active' / 'Connected' are the status values themselves, and 'Connected' and
    // 'Online' share one Hebrew word - as column titles they were ambiguous, and two
    // columns translating to the same title would collide into one.
    'sys___active': 'Active Status',
    'sys___connected': 'Connection Status',
    'sys___online': 'Online',
    'power_consumption': 'Last power consumption [Watt]',
    'lng': 'Longitude',
    'lat': 'Latitude'
  };

  async exportData(format) {
    this.loading = true;
    this.exportedData = [];
    await this.createExportedData();
    let filename;
    if (!this.removeDate) {
      filename = this.sourceTitle + ' - ' + Utils.getDateString();
    } else {
      filename = this.sourceTitle;
    }
    let data;

    switch (format) {
      case 'json':
        data = this.sourceTitle
          ? JSON.stringify(
              [{ source: this.sourceTitle }, ...this.exportedData],
              null,
              '\t'
            )
          : JSON.stringify(this.exportedData, null, '\t');
        break;

      case 'csv':
        // Leading BOM, or Excel reads the UTF-8 Hebrew headers as gibberish.
        // @ts-ignore
        data = '\ufeff' + this.$papa.unparse(this.exportedData);
        break;

      case 'xlsx':
        // Written by exceljs (styled sheet) and saved from its own buffer, so it
        // skips the base64 `download` path below - `btoa` throws on Hebrew values.
        await this.downloadExcel(filename);
        this.loading = false;
        return;
    }

    this.download(filename, format, data);
    this.loading = false;
  }

  /**
   * The default xlsx output was an unstyled sheet with every column at the same
   * default width, so all the values ended up clipped. Columns are sized to their
   * content here, and the header row is styled, frozen and filterable.
   */
  async downloadExcel(filename) {
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet(this.sheet_name, {
      views: [{ state: 'frozen', ySplit: 1, rightToLeft: this.lang !== 'en' }]
    });

    const headers = this.getExcelHeaders();
    if (!headers.length) return;

    sheet.columns = headers.map((header) => ({ header, key: header }));
    sheet.addRows(this.exportedData);

    this.setExcelColumnStyles(sheet);

    const header_row = sheet.getRow(1);
    header_row.height = 24;
    // Per cell, not on the row: a row-level style is written as a style for the whole
    // row, so Excel paints it across every column past the last header as well.
    header_row.eachCell((cell) => {
      cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF292F7D' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    });
    header_row.commit();

    sheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: headers.length }
    };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8;'
    });
    saveAs(blob, `${filename}.xlsx`);
  }

  /**
   * Rows are plain objects and a device may be missing a field the others have, so the
   * header is the union of the keys in the order they first appear. A field that is
   * empty on every single device (a `meta.device` field the project never fills in, for
   * instance) is dropped - it is a titled, blank column and nothing else. If that leaves
   * nothing at all, the columns are kept so the sheet still shows what was asked for.
   */
  getExcelHeaders() {
    const headers = [];
    this.exportedData.forEach((row) =>
      Object.keys(row).forEach((key) => !headers.includes(key) && headers.push(key))
    );
    const filled = headers.filter((header) =>
      this.exportedData.some((row) => {
        const value = row[header];
        return value !== undefined && value !== null && `${value}`.trim() !== '';
      })
    );
    return filled.length ? filled : headers;
  }

  setExcelColumnStyles(sheet) {
    sheet.columns.forEach((column) => {
      let longest = String(column.header).length;
      let scanned = 0;
      column.eachCell({ includeEmpty: false }, (cell) => {
        // A full scan is wasted work on a project-wide export of tens of thousands
        // of devices - the first rows are representative enough of the width.
        if (scanned++ > this.WIDTH_SAMPLE_ROWS) return;
        const length = cell.value === null || cell.value === undefined ? 0 : String(cell.value).length;
        if (length > longest) longest = length;
      });
      column.width = Math.min(Math.max(longest + 4, this.MIN_COLUMN_WIDTH), this.MAX_COLUMN_WIDTH);
      // Excel aligns numbers to the far edge by default, which reads as a ragged
      // column next to the text ones. Every column starts on the reading edge.
      column.alignment = { horizontal: this.text_start, vertical: 'middle' };
    });
  }

  get text_start(): 'left' | 'right' {
    return this.lang === 'en' ? 'left' : 'right';
  }

  // Excel rejects sheet names over 31 chars or containing any of : \ / ? * [ ]
  get sheet_name() {
    const name = (this.sourceTitle || 'Export').replace(/[:\\/?*[\]]/g, ' ').trim();
    return name.slice(0, 31) || 'Export';
  }

  download(filename, format, data) {
    if (data) {
      const blob = new Blob([data], { type: 'text/plain;charset=utf-8;' });
      const link = document.createElement('a');
      let url = null;
      if (format === 'xlsx') {
        url =
          'data:application/octet-stream;charset=utf-16le;base64,' + btoa(data);
      } else {
        url = URL.createObjectURL(blob);
      }
      link.setAttribute('href', url);
      link.setAttribute('download', filename + '.' + format);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  /**
   * The column titles are written in English throughout this component, so the whole
   * row is remapped in one place rather than wrapping every literal. A title with no
   * entry in the locale (MAC, IMEI, ...) falls through unchanged, which is what those
   * identifiers should read as anyway.
   */
  translateHeaders(row) {
    const translated = {};
    Object.entries(row).forEach(([header, value]) => {
      const title = `${this.$t(header)}`;
      // Two titles that translate to the same word would collapse into a single
      // column and drop the other one's data, so the second one stays untranslated.
      translated[translated.hasOwnProperty(title) ? header : title] = value;
    });
    return translated;
  }

  /**
   * The connectivity streams are stored as codes (0 / 1, and -1 / -3 / -4 / -9 for
   * `sys___active`) that mean nothing to whoever opens the file, so they are exported
   * as the status they stand for - the same labels the dashboard shows.
   */
  getFieldValue(device, key, hide_streams) {
    // The map's see-through toggle off: connectivity of a Retrofit device is masked.
    if (hide_streams && this.RETROFIT_MASKED_FIELDS.includes(key)) return this.$t('Retrofit');
    if (key === 'sys___active') return Utils.getActiveStatusLabel(device);
    if (key === 'sys___online') return Utils.getOnlineStatusLabel(device[key]);
    if (key === 'sys___connected') return Utils.getConnectedStatusLabel(device[key]);
    return device[key];
  }

  async createExportedData() {
    if (this.noParsing) {
      this.exportedData = cloneDeep(this.data).map((device_data) => {
        const uppercase_fields = {};
        Object.entries(device_data).forEach(([field_name, value]) => uppercase_fields[PropUtils.getFieldLowerToUpper(field_name)] = value);
        return uppercase_fields;
      });
    } else {
      this.data.map((device) => {
        let dataToExport = {};
        try {
          const meta_device = device['meta.device'];
          const is_retrofit = !Utils.isDeviceLive(device);
          // When the map's see-through toggle is on, the real connectivity
          // streams are exported, but the Status column stays "Retrofit".
          const hide_streams = is_retrofit && !this.$store.state.MapSettings.retrofitSeeThrough;
          const lastSysActiveDate = Utils.convertTimestamp(device.sys___active__modified * 1000, 'DD/MM/YYYY HH:mm', this.timezone);
          const info = {
            'Cabinet ID': meta_device.cabinet_id,
            'Circuit Number': meta_device.circuit_number,
            'Pole Number': meta_device.pole_number,
            // Same labels as the connectivity columns, except that an inactive device
            // also carries the date it went inactive.
            'Status':
              is_retrofit
                ? this.$t('Retrofit')
                : device.sys___active === 0
                  ? `${this.$t('Inactive')} ${this.$t('since')} ${lastSysActiveDate}`
                  : Utils.getActiveStatusLabel(device)
          };
          if (this.isGroups) {
            dataToExport = {
              ID: device.id,
              Name: device.name,
              Location: Utils.deviceLocation(device),
              ...info
            };
          } else {
            const commissionProp = device['meta.commission'];
            const extendedInfo = {
              'Commission': commissionProp.commissioned,
              'Commission time': commissionProp.date,
              'Cabinet ID': device['meta.device'].cabinet_id,
              'Address': Utils.deviceLocation(device)
            };
            if (Utils.hasCabinetClass(device['meta.category'].category)){
              let mac_id = '';
              if (device.class_name.includes('sbc_cabinet')){
                mac_id = PropUtils.getTeltonicaHardwareid(device);
              }else {
                mac_id = device['device.hardwareid'].id || '';
              }
              extendedInfo['CPU ID'] = '';
              extendedInfo['MAC'] = mac_id;
              extendedInfo['IMEI'] = '';
            }else {
              const hardwareidProp = device['device.hardwareid'];
              if (hardwareidProp){
                extendedInfo['CPU ID'] = hardwareidProp.id || '';
                extendedInfo['MAC'] = hardwareidProp.mac || '';
                extendedInfo['IMEI'] = hardwareidProp.imei || '';
              }
            }
            Object.keys(this.headersMap).forEach((key) => {
              dataToExport = {
                ...dataToExport,
                [this.headersMap[key]]: this.getFieldValue(device, key, hide_streams)
              };
            });
            dataToExport = { ...dataToExport, ...info, ...extendedInfo };
          }
          this.exportedData.push(this.translateHeaders(dataToExport));
        } catch (err) {
          console.log('error:', err);
        }
      });
    }
  }

  async getStreamChangeTime(deviceId, streamName) {
    const response = await API.get(
      Types.DEVICES,
      `${deviceId}/streams/${deviceId}.${streamName}/latest/`
    );
    if (!response['timestamp']) {
      return 'N/A';
    }
    const date = new Date(response['timestamp']);
    return Utils.getDateString(date);
  }
}

export default ExportData;
